import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { getOptimalVideoSettings, detectConnectionSpeed } from '../../utils/videoOptimization';

// YouTube Player Component
const YouTubePlayer = ({ youtubeId, autoplay, controls, className }) => (
  <div className={`w-full rounded-lg overflow-hidden ${className}`}>
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=${autoplay ? 1 : 0}&controls=${controls ? 1 : 0}&rel=0&modestbranding=1`}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  </div>
);

// Rumble Player Component
const RumblePlayer = ({ rumbleId, autoplay, className }) => (
  <div className={`w-full rounded-lg overflow-hidden ${className}`}>
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={`https://rumble.com/embed/${rumbleId}/?pub=4`}
        title="Rumble video player"
        frameBorder="0"
        allowFullScreen
      />
    </div>
  </div>
);

// Video.js Player Component
const VideoJSPlayer = ({ videoUrl, onProgress, onTimeUpdate, autoplay, controls, fluid, responsive, aspectRatio, className, userInfo }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const pipVideoRef = useRef(null);
  const containerRef = useRef(null);
  const [isBlurred, setIsBlurred] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent screen recording and screenshots
  useEffect(() => {
    const preventCapture = (e) => {
      e.preventDefault();
      return false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsBlurred(true);
        if (playerRef.current && !document.pictureInPictureElement) {
          playerRef.current.pause();
        }
        setTimeout(() => setIsBlurred(false), 200);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('contextmenu', preventCapture);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Detect screen capture
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
        navigator.mediaDevices.getDisplayMedia = async function(...args) {
          setIsBlurred(true);
          if (playerRef.current) {
            playerRef.current.pause();
          }
          return originalGetDisplayMedia(...args);
        };
      }
    }

    return () => {
      if (container) {
        container.removeEventListener('contextmenu', preventCapture);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [mounted]);

  // Check if URL is from same origin or S3
  const isExternalUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;
      // Don't use crossOrigin for same origin or S3 URLs
      return !url.includes(currentOrigin) && !url.includes('amazonaws.com') && !url.includes('s3.');
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!mounted || !videoRef.current || !videoUrl) {
      console.log('Waiting for mount or videoRef...', { mounted, hasRef: !!videoRef.current, videoUrl });
      return;
    }

    // Check if there's a PiP video playing
    const checkPiPVideo = async () => {
      if (document.pictureInPictureElement) {
        const pipVideo = document.pictureInPictureElement;
        const pipSrc = pipVideo.querySelector('source')?.src || pipVideo.src;
        
        if (pipSrc === videoUrl) {
          // Same video: exit PiP and resume in main player
          pipVideoRef.current = {
            currentTime: pipVideo.currentTime,
            paused: pipVideo.paused
          };
        } else {
          // Different video: stop and exit PiP
          pipVideo.pause();
        }
        await document.exitPictureInPicture();
      }
    };

    checkPiPVideo();

    console.log('Initializing video.js with URL:', videoUrl);

    const connectionSpeed = detectConnectionSpeed();
    const optimalSettings = getOptimalVideoSettings(videoUrl, connectionSpeed);

    const player = videojs(videoRef.current, {
      controls,
      fluid,
      responsive,
      aspectRatio,
      autoplay,
      preload: 'auto',
      playbackRates: [],
      controlBar: {
        playbackRateMenuButton: false,
      },
      ...optimalSettings,
      sources: [{
        src: videoUrl,
        type: (videoUrl && videoUrl.includes('.m3u8')) ? 'application/x-mpegURL' : 'video/mp4'
      }]
    });

    playerRef.current = player;

    player.ready(() => {
      console.log('✅ Player ready');
      
      // Add dynamic moving watermark
      if (userInfo) {
        const playerEl = player.el();
        const watermark = document.createElement('div');
        watermark.className = 'vjs-watermark-dynamic';
        watermark.textContent = userInfo;
        watermark.style.cssText = `
          position: absolute;
          color: rgba(255, 255, 255, 0.7);
          font-size: 13px;
          font-weight: 500;
          text-shadow: 0 1px 4px rgba(0,0,0,0.9);
          pointer-events: none;
          z-index: 9999;
          user-select: none;
          -webkit-user-select: none;
          transition: all 0.5s ease;
          opacity: 0;
        `;
        playerEl.appendChild(watermark);
        
        // Random position generator
        const positions = [
          { top: '20px', left: '20px' },
          { top: '20px', right: '20px' },
          { top: '20px', left: '50%', transform: 'translateX(-50%)' },
          { bottom: '60px', left: '20px' },
          { bottom: '60px', right: '20px' },
          { bottom: '60px', left: '50%', transform: 'translateX(-50%)' },
          { top: '50%', left: '20px', transform: 'translateY(-50%)' },
          { top: '50%', right: '20px', transform: 'translateY(-50%)' },
          { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
          { top: '30%', left: '30%' },
          { top: '30%', right: '30%' },
          { top: '70%', left: '30%' },
          { top: '70%', right: '30%' }
        ];
        
        const moveWatermark = () => {
          // Show watermark
          const pos = positions[Math.floor(Math.random() * positions.length)];
          watermark.style.top = pos.top || 'auto';
          watermark.style.bottom = pos.bottom || 'auto';
          watermark.style.left = pos.left || 'auto';
          watermark.style.right = pos.right || 'auto';
          watermark.style.transform = pos.transform || 'none';
          watermark.style.opacity = '1';
          
          // Hide after 2 seconds
          setTimeout(() => {
            watermark.style.opacity = '0';
          }, 2000);
        };
        
        // Show immediately
        moveWatermark();
        
        // Move every 4 seconds (2s visible + 2s hidden)
        const interval = setInterval(moveWatermark, 4000);
        
        // Store interval for cleanup
        playerEl._watermarkInterval = interval;
      }
      
      // Resume from PiP position if available
      if (pipVideoRef.current) {
        player.currentTime(pipVideoRef.current.currentTime);
        if (!pipVideoRef.current.paused) {
          player.play();
        }
        pipVideoRef.current = null;
      }
    });

    player.on('timeupdate', () => {
      const currentTime = player.currentTime();
      const duration = player.duration();
      if (duration > 0) {
        const progress = (currentTime / duration) * 100;
        onProgress?.(progress, currentTime, duration);
        onTimeUpdate?.(currentTime, duration);
      }
    });

    player.on('error', () => {
      const error = player.error();
      if (error) {
        console.error('❌ Video error:', error.code, error.message);
      }
    });

    return () => {
      console.log('Cleaning up video.js player');
      if (playerRef.current) {
        const videoElement = playerRef.current.el()?.querySelector('video');
        const isInPiP = videoElement && document.pictureInPictureElement === videoElement;
        
        // Clear watermark interval
        const playerEl = playerRef.current.el();
        if (playerEl?._watermarkInterval) {
          clearInterval(playerEl._watermarkInterval);
        }
        
        // Remove watermark
        const watermark = playerEl?.querySelector('.vjs-watermark-dynamic');
        if (watermark) watermark.remove();
        
        if (!isInPiP) {
          try {
            playerRef.current.dispose();
          } catch (e) {
            console.warn('Dispose error:', e);
          }
          playerRef.current = null;
        }
      }
    };
  }, [mounted, videoUrl]);

  const needsCrossOrigin = isExternalUrl(videoUrl);

  return (
    <div ref={containerRef} className={`w-full rounded-lg overflow-hidden ${className}`} style={{ userSelect: 'none', WebkitUserSelect: 'none', position: 'relative' }}>
      <video
        ref={videoRef}
        className="video-js vjs-default-skin"
        playsInline
        controlsList="nodownload"
        disablePictureInPicture={false}
        {...(needsCrossOrigin && { crossOrigin: 'anonymous' })}
        onContextMenu={(e) => e.preventDefault()}
        style={{ pointerEvents: 'auto', filter: isBlurred ? 'blur(50px)' : 'none', transition: 'filter 0.1s' }}
      />
      {isBlurred && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000',
          zIndex: 9999
        }} />
      )}
    </div>
  );
};

const SecureVideoPlayer = ({ 
  videoUrl, 
  onProgress, 
  onTimeUpdate, 
  className = '',
  autoplay = false,
  controls = true,
  fluid = true,
  responsive = true,
  aspectRatio = '16:9',
  userInfo = null
}) => {
  const [videoType, setVideoType] = useState(null);
  const [youtubeId, setYoutubeId] = useState(null);
  const [rumbleId, setRumbleId] = useState(null);

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Extract Rumble video ID from URL
  const getRumbleId = (url) => {
    const regExp = /rumble\.com\/(v[a-z0-9]+)/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  useEffect(() => {
    if (!videoUrl) {
      setVideoType(null);
      return;
    }
    
    const ytId = getYouTubeId(videoUrl);
    if (ytId) {
      setVideoType('youtube');
      setYoutubeId(ytId);
      return;
    }

    const rId = getRumbleId(videoUrl);
    if (rId) {
      setVideoType('rumble');
      setRumbleId(rId);
      return;
    }

    setVideoType('regular');
    setYoutubeId(null);
    setRumbleId(null);
  }, [videoUrl]);

  if (!videoType) return null;

  if (videoType === 'youtube') {
    return <YouTubePlayer key={`yt-${youtubeId}`} youtubeId={youtubeId} autoplay={autoplay} controls={controls} className={className} />;
  }

  if (videoType === 'rumble') {
    return <RumblePlayer key={`rumble-${rumbleId}`} rumbleId={rumbleId} autoplay={autoplay} className={className} />;
  }

  return (
    <VideoJSPlayer
      key={`vjs-${videoUrl}`}
      videoUrl={videoUrl}
      onProgress={onProgress}
      onTimeUpdate={onTimeUpdate}
      autoplay={autoplay}
      controls={controls}
      fluid={fluid}
      responsive={responsive}
      aspectRatio={aspectRatio}
      className={className}
      userInfo={userInfo}
    />
  );
};

export default SecureVideoPlayer;
